import { Link } from '@tanstack/react-router'
import type { ICellRendererParams } from 'ag-grid-community'
import { type ReactNode, useMemo, useState } from 'react'

import type {
	InventoryLevelListItem,
	InventoryStockStatusType,
	InventoryVariantSearchItem
} from '@/api/admin-inventory-types'
import { useUpdateProductVariant } from '@/api/api-mutations'
import { useSearchInventoryVariants } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Input } from '@/components/base/input'
import { Label } from '@/components/base/label'
import { StatusCell, type StatusColor } from '@/components/base/status-cell'
import { Switch } from '@/components/base/switch'
import { toast } from '@/components/base/toast'
import { Tooltip } from '@/components/base/tooltip'
import { type TableColDef } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'
import { ExclamationCircle } from '@/icons/exclamation-circle'
import { fromPrecision } from '@/utils/currency'

const TrackingToggleCell = ({ data }: ICellRendererParams<InventoryLevelListItem>) => {
	const updateVariant = useUpdateProductVariant(true)
	if (!data) return null
	const { trackInventory, productUuid, uuid: variantUuid } = data.variant
	return (
		<div className='flex h-full items-center' onClick={e => e.stopPropagation()}>
			<Switch
				size='small'
				checked={trackInventory}
				disabled={updateVariant.isPending}
				onCheckedChange={checked =>
					updateVariant.mutateAsync(
						{
							path: { productUuid, variantUuid },
							body: { trackInventory: checked }
						},
						{
							onError: error => toast.error(error.message)
						}
					)
				}
			/>
		</div>
	)
}

export const getStockStatusColor = (status: InventoryStockStatusType): StatusColor => {
	switch (status) {
		case 'IN_STOCK':
			return 'green'
		case 'LOW_STOCK':
			return 'orange'
		case 'OUT_OF_STOCK':
			return 'red'
		default:
			return 'grey'
	}
}

export const formatMarginPercent = (value?: number) => {
	if (value == null) return '—'
	return `${value.toFixed(1)}%`
}

export const formatAmount = (amount?: { value: number; precision: number }, currencyCode?: string) => {
	if (!amount) return '—'
	const value = fromPrecision(amount)
	return currencyCode ? `${value} ${currencyCode}` : String(value)
}

export const useInventoryLevelColumnDefs = () => {
	const { L } = useI18n()

	return useMemo<TableColDef<InventoryLevelListItem>[]>(
		() => [
			{
				headerName: L.Inventory.Column.Product(),
				field: 'variant.productTitle',
				minWidth: 180,
				flex: 2
			},
			{
				headerName: L.Inventory.Column.Sku(),
				field: 'variant.sku',
				minWidth: 120,
				flex: 1
			},
			{
				headerName: L.Inventory.Column.InventoryLocation(),
				field: 'inventoryLocation.name',
				minWidth: 140,
				flex: 1
			},
			{
				headerName: L.Inventory.Column.Supplier(),
				colId: 'preferredSupplier',
				minWidth: 160,
				flex: 1,
				suppressRowLink: true,
				valueGetter: ({ data }) => data?.preferredSupplier?.name,
				cellRenderer: ({ data }: ICellRendererParams<InventoryLevelListItem>) => {
					if (!data) return null
					return (
						<div className='flex h-full items-center gap-2'>
							<span className='truncate'>{data.preferredSupplier?.name ?? '—'}</span>
							<Link
								to='/inventory/$inventoryLevelUuid/suppliers'
								params={{ inventoryLevelUuid: data.uuid }}
								onClick={e => e.stopPropagation()}
							>
								<Button size='small' variant='transparent' className='shrink-0'>
									{L.Inventory.ManageSuppliers()}
								</Button>
							</Link>
						</div>
					)
				}
			},
			{
				headerName: L.Inventory.Column.Margin(),
				headerTooltip: L.Inventory.SupplierDialog.MarginTaxNote(),
				colId: 'margin',
				minWidth: 90,
				flex: 1,
				suppressRowLink: true,
				valueGetter: ({ data }) => data?.margin?.marginPercent,
				cellRenderer: ({ data }: ICellRendererParams<InventoryLevelListItem>) =>
					data?.margin?.currencyMismatch ? (
						<Tooltip
							content={L.Inventory.SupplierDialog.MarginCurrencyMismatch({
								retail: data.margin.retailCurrencyCode ?? '?',
								purchase: data.margin.currencyCode ?? '?'
							})}
						>
							<button
								type='button'
								className='inline-flex items-center gap-1 text-ui-fg-subtle'
								aria-label={L.Inventory.SupplierDialog.MarginCurrencyMismatch({
									retail: data.margin.retailCurrencyCode ?? '?',
									purchase: data.margin.currencyCode ?? '?'
								})}
							>
								—
								<ExclamationCircle className='text-ui-fg-muted' />
							</button>
						</Tooltip>
					) : (
						formatMarginPercent(data?.margin?.marginPercent)
					)
			},
			{
				headerName: L.Inventory.Column.PurchasePrice(),
				headerTooltip: L.Inventory.SupplierDialog.PurchasePriceTaxNote(),
				colId: 'purchasePrice',
				minWidth: 120,
				flex: 1,
				valueGetter: ({ data }) => data?.margin?.purchasePrice,
				cellRenderer: ({ data }: ICellRendererParams<InventoryLevelListItem>) =>
					formatAmount(data?.margin?.purchasePrice, data?.margin?.currencyCode)
			},
			{
				headerName: L.Inventory.Column.QuantityOnHand(),
				field: 'quantityOnHand',
				minWidth: 100,
				flex: 1
			},
			{
				headerName: L.Inventory.Column.ReorderPoint(),
				field: 'reorderPoint',
				minWidth: 120,
				flex: 1
			},
			{
				headerName: L.Inventory.Column.Status(),
				field: 'stockStatus',
				minWidth: 120,
				flex: 1,
				cellRenderer: ({ data, value }: ICellRendererParams<InventoryLevelListItem>) => {
					if (data && !data.variant.trackInventory) {
						return <StatusCell color='grey'>{L.Inventory.NotTracked()}</StatusCell>
					}
					return (
						<StatusCell color={getStockStatusColor(value as InventoryStockStatusType)}>
							{L.Inventory.Status[value as InventoryStockStatusType]()}
						</StatusCell>
					)
				}
			},
			{
				headerName: L.Inventory.Column.Tracked(),
				colId: 'trackInventory',
				minWidth: 90,
				flex: 1,
				suppressRowLink: true,
				valueGetter: ({ data }) => data?.variant.trackInventory,
				cellRenderer: TrackingToggleCell
			}
		],
		[L]
	)
}

export const useReportLowStockColumnDefs = () => {
	const { L } = useI18n()

	return useMemo<TableColDef<InventoryLevelListItem>[]>(
		() => [
			{
				headerName: L.Inventory.Column.Product(),
				field: 'variant.productTitle',
				minWidth: 180,
				flex: 2
			},
			{
				headerName: L.Inventory.Column.InventoryLocation(),
				field: 'inventoryLocation.name',
				minWidth: 140,
				flex: 1
			},
			{
				headerName: L.Inventory.Column.QuantityOnHand(),
				field: 'quantityOnHand',
				minWidth: 100,
				flex: 1
			},
			{
				headerName: L.Inventory.Column.Status(),
				field: 'stockStatus',
				minWidth: 120,
				flex: 1,
				cellRenderer: ({ data, value }: ICellRendererParams<InventoryLevelListItem>) => {
					if (data && !data.variant.trackInventory) {
						return <StatusCell color='grey'>{L.Inventory.NotTracked()}</StatusCell>
					}
					return (
						<StatusCell color={getStockStatusColor(value as InventoryStockStatusType)}>
							{L.Inventory.Status[value as InventoryStockStatusType]()}
						</StatusCell>
					)
				}
			}
		],
		[L]
	)
}

export type VariantDraftLine = {
	variantUuid: string
	productTitle: string
	sku?: string
	quantity: string
}

export function useVariantLineDraft<TLine extends VariantDraftLine>(options: {
	active: boolean
	inventoryLocationUuid: string
	createLine: (base: VariantDraftLine) => TLine
}) {
	const { active, inventoryLocationUuid, createLine } = options
	const [search, setSearch] = useState('')
	const [lines, setLines] = useState<TLine[]>([])

	const variantResults = useSearchInventoryVariants(
		{ q: search, inventoryLocationUuid, limit: 8 },
		{ enabled: active && inventoryLocationUuid !== '' && search.trim().length > 0 }
	)

	const addLine = (variantUuid: string, productTitle: string, sku?: string) => {
		setLines(prev =>
			prev.some(l => l.variantUuid === variantUuid)
				? prev
				: [...prev, createLine({ variantUuid, productTitle, sku, quantity: '1' })]
		)
		setSearch('')
	}

	const updateLine = (variantUuid: string, patch: Partial<TLine>) =>
		setLines(prev => prev.map(l => (l.variantUuid === variantUuid ? { ...l, ...patch } : l)))

	const removeLine = (variantUuid: string) => setLines(prev => prev.filter(l => l.variantUuid !== variantUuid))

	const reset = () => {
		setSearch('')
		setLines([])
	}

	return { search, setSearch, lines, addLine, updateLine, removeLine, reset, variantResults }
}

export function VariantSearchInput({
	search,
	setSearch,
	disabled,
	results,
	onSelect,
	renderResultMeta
}: {
	search: string
	setSearch: (value: string) => void
	disabled: boolean
	results: InventoryVariantSearchItem[]
	onSelect: (variant: InventoryVariantSearchItem) => void
	renderResultMeta?: (variant: InventoryVariantSearchItem) => ReactNode
}) {
	const { L } = useI18n()
	return (
		<div className='border-t border-ui-border-base pt-3'>
			<Label>{L.Inventory.Column.Product()}</Label>
			<Input
				placeholder={L.General.SearchPlaceholder()}
				value={search}
				disabled={disabled}
				onChange={e => setSearch(e.target.value)}
			/>
			{!disabled && search.trim().length > 0 && (
				<div className='mt-1 max-h-48 overflow-y-auto rounded-md border border-ui-border-base'>
					{results.map(variant => (
						<button
							key={variant.uuid}
							type='button'
							className='flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-ui-bg-subtle'
							onClick={() => onSelect(variant)}
						>
							<span className='truncate'>{variant.productTitle}</span>
							{renderResultMeta
								? renderResultMeta(variant)
								: variant.sku && <span className='text-xs text-ui-fg-muted'>{variant.sku}</span>}
						</button>
					))}
					{results.length === 0 && <div className='px-3 py-2 text-sm text-ui-fg-muted'>—</div>}
				</div>
			)}
		</div>
	)
}
